import { getAuthHeaders, isAuthenticated } from './auth';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.centroautomotrizhlfrd.com.do/api';

const redirectToLogin = () => {
  localStorage.removeItem('client-auth-storage');
  window.location.href = '/login';
};

const apiRequest = async (url: string, options: RequestInit = {}): Promise<Response> => {
  if (!isAuthenticated()) {
    redirectToLogin();
    throw new Error('No hay sesión activa');
  }

  const config: RequestInit = {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers
    }
  };

  const response = await fetch(url, config);

  if (response.status === 401) {
    redirectToLogin();
    throw new Error('Sesión expirada');
  }

  return response;
};

export interface Appointment {
  id: string;
  appointmentDate: string;
  status: 'scheduled' | 'in_progress' | 'completed' | 'canceled';
  notes?: string;
  services: {
    id: number;
    name: string;
    price: number;
    duration: number;
    category: {
      name: string;
      color: string;
    };
  }[];
  totalPrice: number;
  totalDuration: number;
  mechanic?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateAppointmentData {
  serviceIds: number[];
  appointmentDate: string;
  notes?: string;
  vehicleId?: string;
}

export interface UpdateAppointmentData {
  appointmentDate?: string;
  notes?: string;
}

export interface AvailabilityRequest {
  date: string; // YYYY-MM-DD
  time?: string; // HH:MM
}

export interface AvailabilityResponse {
  available: boolean;
  reason?: string;
  currentCount?: number;
  maxCapacity?: number;
  remainingSlots?: number;
}

export interface TimeSlot {
  time: string; // HH:MM format
  available: boolean;
  currentCount: number;
  maxCapacity: number;
  remainingSlots: number;
}

export interface AvailableTimeSlotsResponse {
  date: string;
  dayOfWeek: number;
  available: boolean;
  availableSlots: TimeSlot[];
}

export interface ActiveAppointmentResponse {
  hasActiveAppointment: boolean;
  appointment?: Appointment;
}


// Función para obtener la cita activa del cliente
export const getActiveAppointment = async (): Promise<ActiveAppointmentResponse> => {
  try {
    const response = await apiRequest(`${API_BASE_URL}/client/appointments/active`);

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching active appointment:', error);
    // En caso de error, asumir que no hay cita activa
    return { hasActiveAppointment: false };
  }
};

// Función para obtener todas las citas del cliente
export const getAppointments = async (): Promise<Appointment[]> => {
  try {
    const response = await apiRequest(`${API_BASE_URL}/client/appointments`);

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    const appointments = await response.json();
    return appointments;
  } catch (error) {
    console.error('Error fetching appointments:', error);
    throw error;
  }
};

// Función para crear una nueva cita
export const createAppointment = async (data: CreateAppointmentData): Promise<Appointment> => {
  console.log('📡 createAppointment called with:', {
    serviceIds: data.serviceIds,
    serviceCount: data.serviceIds?.length,
    appointmentDate: data.appointmentDate,
    vehicleId: data.vehicleId
  });

  try {
    const response = await apiRequest(`${API_BASE_URL}/client/appointments`, {
      method: 'POST',
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
    }

    const appointment = await response.json();
    return appointment;
  } catch (error) {
    console.error('Error creating appointment:', error);
    throw error;
  }
};

// Función para actualizar una cita
export const updateAppointment = async (id: string, data: UpdateAppointmentData): Promise<Appointment> => {
  try {
    const response = await apiRequest(`${API_BASE_URL}/client/appointments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
    }

    const appointment = await response.json();
    return appointment;
  } catch (error) {
    console.error('Error updating appointment:', error);
    throw error;
  }
};

// Función para cancelar una cita
export const cancelAppointment = async (id: string): Promise<void> => {
  try {
    const response = await apiRequest(`${API_BASE_URL}/client/appointments/${id}`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
    }
  } catch (error) {
    console.error('Error canceling appointment:', error);
    throw error;
  }
};

// Función para verificar disponibilidad de fecha/hora
export const checkAvailability = async (data: AvailabilityRequest): Promise<AvailabilityResponse> => {
  try {
    const response = await apiRequest(`${API_BASE_URL}/client/appointments/availability`, {
      method: 'POST',
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
    }

    const availability = await response.json();
    return availability;
  } catch (error) {
    console.error('Error checking availability:', error);
    // En caso de error, retornar disponible para no bloquear al usuario
    return {
      available: true,
      reason: 'Error verificando disponibilidad - contacta al taller'
    };
  }
};

// Función para obtener horarios disponibles para una fecha específica
export const getAvailableTimeSlots = async (date: string): Promise<AvailableTimeSlotsResponse> => {
  try {
    const response = await apiRequest(`${API_BASE_URL}/client/appointments/available-slots?date=${encodeURIComponent(date)}`, {
      method: 'GET'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error getting available time slots:', error);
    // En caso de error, retornar lista vacía para forzar que se muestre mensaje de error
    return {
      date,
      dayOfWeek: 0,
      available: false,
      availableSlots: []
    };
  }
};


// Función para formatear la fecha de la cita
export const formatAppointmentDate = (dateString: string): string => {
  // dateString viene del servidor como YYYY-MM-DDTHH:MM:SS (sin Z, sin offset)
  // Evitar new Date(dateString) porque el navegador lo interpreta como UTC y suma +4h
  const [datePart, timePart] = dateString.split('T');
  if (!datePart || !timePart) return dateString;

  const [year, month, day] = datePart.split('-').map(n => parseInt(n, 10));
  const [hour, minute] = timePart.split(':').slice(0, 2).map(n => parseInt(n, 10));

  // Construimos una fecha local usando Intl para formatear, sin conversión de zona horaria
  // Usamos una fecha en la zona local y pedimos formateo en America/Santo_Domingo
  const local = new Date(year, (month - 1), day, hour, minute, 0, 0);

  return new Intl.DateTimeFormat('es-ES', {
    timeZone: 'America/Santo_Domingo',
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(local);
};

// Función para obtener el estado de la cita en español
export const getAppointmentStatusText = (status: string): string => {
  const statusMap: Record<string, string> = {
    scheduled: 'Programada',
    in_progress: 'En Progreso',
    completed: 'Completada',
    canceled: 'Cancelada'
  };
  return statusMap[status] || status;
};

// Función para obtener el color del estado
export const getAppointmentStatusColor = (status: string): string => {
  const colorMap: Record<string, string> = {
    scheduled: 'bg-blue-100 text-blue-800',
    in_progress: 'bg-yellow-100 text-yellow-800',
    completed: 'bg-green-100 text-green-800',
    canceled: 'bg-red-100 text-red-800'
  };
  return colorMap[status] || 'bg-gray-100 text-gray-800';
};
